/**
 * §P.2 · Los grupos del club.
 *
 * **La ocupación va en la lista, no dentro del detalle.** `BFF_BO.md` §7.5:
 * «`enrolled_count` con `capacity` es "18 de 20": pintadlo **en la lista** — es lo
 * que necesita saber quien atiende la llamada de una familia nueva». Con eso en
 * pantalla, quien coge el teléfono puede ofrecer otro grupo en la misma
 * conversación sin abrir nada.
 *
 * Y los grupos cerrados **existen**: cerrar no borra, porque de un grupo cuelgan
 * las sesiones del curso y la asistencia de cada niño. Se pueden ver con el
 * interruptor.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  estaCompleto,
  ocupacion,
  rangoDeEdad,
  type Grupo,
} from '@/club/grupos/tipos';
import { FormularioDeGrupo } from '@/club/grupos/FormularioDeGrupo';

export function ListaDeGrupos({
  club,
  puedeEscribir,
  suspendido,
  alAbrir,
  empezarCreando = false,
}: {
  club: string;
  puedeEscribir: boolean;
  suspendido: boolean;
  alAbrir: (grupoId: string) => void;
  /** Abrir directamente el alta (`?nuevo=1`). */
  empezarCreando?: boolean;
}) {
  const [creando, setCreando] = useState(empezarCreando);
  const [conCerrados, setConCerrados] = useState(false);

  const grupos = useQuery({
    queryKey: ['grupos', club, conCerrados],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: conCerrados ? { include_closed: true } : {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  if (creando) {
    return (
      <FormularioDeGrupo
        club={club}
        grupo={null}
        etag={null}
        suspendido={suspendido}
        alGuardar={(id) => {
          setCreando(false);
          alAbrir(id);
        }}
        alCancelar={() => setCreando(false)}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Grupos</h1>
        {puedeEscribir && (
          <button type="button" onClick={() => setCreando(true)}>
            Nuevo grupo
          </button>
        )}
      </header>

      <label className="interruptor">
        <input
          type="checkbox"
          checked={conCerrados}
          onChange={(e) => setConCerrados(e.target.checked)}
        />
        Enseñar también los grupos cerrados
      </label>

      {grupos.isPending && <Cargando filas={4} etiqueta="Cargando grupos" />}

      {grupos.isError && (
        <PanelError
          problema={aProblema(grupos.error)}
          reintentar={() => void grupos.refetch()}
        />
      )}

      {grupos.data?.length === 0 && (
        <Vacio
          titulo="Todavía no hay grupos"
          explicacion="Un grupo es una clase que se repite: «Infantil lunes y miércoles a las 17:30». De él salen las sesiones del curso y a él se matriculan los alumnos."
          {...(puedeEscribir
            ? {
                accion: (
                  <button type="button" onClick={() => setCreando(true)}>
                    Crear el primer grupo
                  </button>
                ),
              }
            : {})}
        />
      )}

      {grupos.data && grupos.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Sede</th>
                <th>Edades</th>
                <th>Ocupación</th>
              </tr>
            </thead>
            <tbody>
              {grupos.data.map((g) => (
                <tr
                  key={g.id}
                  className={`fila-pulsable${g.active ? '' : ' fila-fuera'}`}
                  onClick={() => alAbrir(g.id)}
                >
                  <td>
                    <button type="button" className="enlace-fila">
                      {g.name}
                    </button>
                    {!g.active && (
                      <span className="pastilla pastilla-left">Cerrado</span>
                    )}
                    {/* Lo que busca quien llega desde «Ponles horario» de Inicio. */}
                    {g.active && g.schedule_slots === 0 && (
                      <>
                        {' '}
                        <span className="pastilla pastilla-paused">
                          Sin horario
                        </span>
                      </>
                    )}
                    {g.level && <span className="tenue"> · {g.level}</span>}
                  </td>
                  <td className="tenue">{g.venue_name ?? '—'}</td>
                  {/* Se enseña, no se impone: el servidor no valida la edad. */}
                  <td className="tenue">{rangoDeEdad(g) ?? '—'}</td>
                  <td>
                    <span
                      className={
                        estaCompleto(g)
                          ? 'pastilla pastilla-paused'
                          : 'pastilla pastilla-active'
                      }
                    >
                      {ocupacion(g)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
